import {
    getLocaleOverride,
    gettext,
    setLanguage,
    setup as setupI18n,
} from '@eventbrite/i18n';
import * as Sentry from '@sentry/react';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { App } from './app';
import type { EventDescriptionProps } from './components/EventInfo/EventDescription/EventDescription';
import { EventInfo } from './components/EventInfo/EventInfo';
import type { EventMapLayoutProps } from './components/EventMap/EventMapLayout';
import { EventMapLayout } from './components/EventMap/EventMapLayout';
import type { OrganizerPanelLayoutProps } from './components/OrganizerPanel/OrganizerPanelLayout';
import { OrganizerPanelLayout } from './components/OrganizerPanel/OrganizerPanelLayout';
import type { ShareInlineProps } from './components/ShareInline/ShareInline';
import type { Tag } from './components/Tags/Tags';
import type { AppProps, Event, EventDetailsProps, Settings } from './types';

export const ServerApp = ({
    event,
    eventDescription,
    eventDetails,
    tags,
    settings,
    shareInline,
    organizerPanelLayout,
    eventMapLayout,
}: {
    event: Event;
    eventDescription: EventDescriptionProps;
    eventDetails: EventDetailsProps;
    tags: Array<Tag>;
    settings: Settings;
    shareInline: ShareInlineProps;
    organizerPanelLayout: OrganizerPanelLayoutProps;
    eventMapLayout: EventMapLayoutProps;
}) => {
    return (
        <Sentry.ErrorBoundary
            fallback={<>{gettext('Error on Listings SSR')}</>}
        >
            <>
                {settings.featureFlags?.enable_ssr_for_event_hero && <></>}
                {settings.featureFlags?.enable_ssr_for_conversion_bar && <></>}
                <EventInfo
                    {...{
                        description: { ...eventDescription },
                        tags,
                        shareInline: { ...shareInline },
                        eventDetails: {
                            ...{
                                ...eventDetails,
                                location: {
                                    ...eventDetails.location,
                                    isOnlineEvent: event.isOnlineEvent,
                                    onlineEventUrl:
                                        eventDetails.onlineEvent?.url,
                                },
                                dates: {
                                    ...eventDetails.dates,
                                    isSeriesParent: event.isSeriesParent,
                                    childEvents: event.childEvents || [],
                                },
                            },
                        },
                    }}
                />
                {settings.featureFlags
                    ?.enable_ssr_for_organizer_info_and_event_map && (
                    <>
                        <EventMapLayout {...eventMapLayout} />
                        <OrganizerPanelLayout {...organizerPanelLayout} />
                    </>
                )}
            </>
        </Sentry.ErrorBoundary>
    );
};

export const render = (props: AppProps) => {
    const locale = getLocaleOverride() || props.env.localeInfo.locale;
    setupI18n(require.context('./i18n/translations', true, /\.json$/));
    setLanguage(locale);

    return renderToString(
        <ServerApp
            event={{ ...props.event }}
            eventDescription={{ ...props.components.eventDescription }}
            eventDetails={{ ...props.components.eventDetails }}
            tags={props.components.tags}
            shareInline={props.components.shareInline}
            settings={props.settings}
            organizerPanelLayout={props.components.organizerPanelLayout}
            eventMapLayout={props.components.eventMap}
        />,
    );
};

export default App;
